import { Component, Inject, OnInit } from "@angular/core";
import {
  MatTableDataSource,
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService } from "src/_services/app.service";
import { OperatorLimitProfile } from "src/_models";
import { SapRoles } from "src/_constants/roleType";
import { LimitExtrasLabel, LimitStakesLabel } from "src/_helpers/utils";

@Component({
  selector: "app-limits-management",
  templateUrl: "./limits-management.component.html",
  styleUrls: ["./limits-management.component.scss"],
})
export class LimitsManagementComponent implements OnInit {
  limitsDisplayedColumns: string[] = [
    "Name",
    "MatchOdds",
    "Premium",
    "Bookmaker",
    "Fancy",
    "Actions",
  ];

  readonly defaultOperatorId = "SAPAdmin";
  limits = new MatTableDataSource<OperatorLimitProfile>();
  userRole: any = JSON.parse(sessionStorage.getItem("currentUser"));
  sapRoles = SapRoles;
  OperatorId: string = "";
  operatorData = [];
  dropdownSettings: any = {};
  opId = new FormControl([]);

  constructor(
    private appService: AppService,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.dropdownSettings = {
      singleSelection: true,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.getOperatorData();
  }

  stakesLabel(section: any): string {
    return LimitStakesLabel(section);
  }

  extrasLabel(section: any): string {
    return LimitExtrasLabel(section);
  }

  get showOperatorDropdown(): boolean {
    return [
      this.sapRoles.SAPAdmin,
      this.sapRoles.SAPUser,
      this.sapRoles.RiskAdmin,
      this.sapRoles.GroupAdmin,
    ].includes(this.userRole.role);
  }

  withDefaultOperator(operators: string[]): string[] {
    const list = (operators || []).filter((op) => !!op && op !== this.defaultOperatorId);
    return [this.defaultOperatorId, ...list];
  }

  getOperatorData() {
    if (this.userRole.role === this.sapRoles.GroupAdmin) {
      this.operatorData = this.userRole.operators || [];
      this.selectInitialOperator();
      return;
    }

    if (
      this.userRole.role === this.sapRoles.SAPAdmin ||
      this.userRole.role === this.sapRoles.SAPUser ||
      this.userRole.role === this.sapRoles.RiskAdmin
    ) {
      try {
        this.appService.getOperatorId().subscribe((res) => {
          if (res.status === "RS_OK") {
            const operators = (res.operators || [])
              .sort((a, b) => {
                const fa = (a.operator_id || "").toLowerCase();
                const fb = (b.operator_id || "").toLowerCase();
                if (fa < fb) {
                  return -1;
                }
                if (fa > fb) {
                  return 1;
                }
                return 0;
              })
              .map((operator) => operator.operator_id);
            this.operatorData = this.withDefaultOperator(operators);
            this.selectInitialOperator();
          } else {
            this.appService.openErrorBar(res.errorDescription);
            if (res.errorDescription === "Invalid Token!") {
              sessionStorage.clear();
              this.router.navigateByUrl("/login");
            }
          }
        });
      } catch (err) { }
      return;
    }

    this.OperatorId = this.userRole.operatorId;
    this.getOperatorLimits();
  }

  selectInitialOperator() {
    if (!this.operatorData.length) {
      this.OperatorId = "";
      this.limits.data = [];
      return;
    }
    const incomingOperatorId = this.route.snapshot.queryParamMap.get("OperatorId");
    let preselected = this.defaultOperatorId;
    if (incomingOperatorId && this.operatorData.includes(incomingOperatorId)) {
      preselected = incomingOperatorId;
    } else if (!this.operatorData.includes(this.defaultOperatorId)) {
      preselected = this.operatorData[0];
    }
    this.onSelectedOperatorId(preselected);
  }

  onSelectedOperatorId = (oid) => {
    this.OperatorId = oid;
    this.opId.setValue([oid]);
    this.getOperatorLimits();
  };

  getOperatorLimits() {
    if (!this.OperatorId) {
      this.limits.data = [];
      return;
    }
    try {
      this.appService
        .getOperatorLimits({ OperatorId: this.OperatorId })
        .subscribe((res) => {
          if (res.status === "RS_OK") {
            this.limits.data = res.limits;
          } else {
            this.appService.openErrorBar(res.errorDescription);
            if (res.errorDescription === "Invalid Token!") {
              sessionStorage.clear();
              this.router.navigateByUrl("/login");
            }
          }
        });
    } catch (err) { }
  }

  addLimit() {
    if (!this.OperatorId) {
      this.appService.openErrorBar("Please select an operator first");
      return;
    }
    const dialogRef = this.dialog.open(AddEditLimit, {
      backdropClass: "backdropBackground",
      hasBackdrop: true,
      width: "600px",
      panelClass: "limit-dialog-panel",
      data: {
        add: true,
        OperatorId: this.OperatorId,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getOperatorLimits();
      }
    });
  }

  editLimit(element: OperatorLimitProfile) {
    const dialogRef = this.dialog.open(AddEditLimit, {
      backdropClass: "backdropBackground",
      hasBackdrop: true,
      width: "600px",
      panelClass: "limit-dialog-panel",
      data: {
        add: false,
        OperatorId: this.OperatorId,
        id: element.id,
        name: element.name,
        config: element.config,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getOperatorLimits();
      }
    });
  }
}

@Component({
  selector: "app-add-edit-limit",
  templateUrl: "./add-edit-limit.html",
  styleUrls: ["./limits-management.component.scss"],
})
export class AddEditLimit implements OnInit {
  limitForm: FormGroup;
  sections = [
    { key: "matchOdds", title: "Match Odds" },
    { key: "premium", title: "Premium" },
    { key: "bookmaker", title: "Bookmaker" },
    { key: "fancy", title: "Fancy" },
  ];

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    public router: Router,
    public dialogRef: MatDialogRef<AddEditLimit>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit() {
    const config = this.data.config || {};
    const group: any = {
      name: [this.data.name || "", [Validators.required]],
    };
    this.sections.forEach((section) => {
      group[section.key] = this.buildSection(config[section.key]);
    });
    this.limitForm = this.fb.group(group);
  }

  // Min/max are a set - both 0 keeps the stakes the operator hierarchy already
  // resolves - while delay and odd limits override on their own, so a profile
  // can carry a delay and nothing else.
  buildSection(values: any): FormGroup {
    return this.fb.group(
      {
        min: [values ? values.min || 0 : 0, [Validators.required, Validators.min(0)]],
        max: [values ? values.max || 0 : 0, [Validators.required, Validators.min(0)]],
        delay: [values ? values.delay || 0 : 0, [Validators.required, Validators.min(0)]],
        oddLimits: [values ? values.oddLimits || 0 : 0, [Validators.required, Validators.min(0)]],
      },
      { validator: stakePairValidator }
    );
  }

  confirm() {
    if (this.limitForm.invalid) {
      this.markFormGroupTouched(this.limitForm);
      return;
    }
    const formValue = this.limitForm.value;
    const config: any = {};
    this.sections.forEach((section) => {
      const values = formValue[section.key];
      config[section.key] = {
        min: Number(values.min) || 0,
        max: Number(values.max) || 0,
        delay: Number(values.delay) || 0,
        // The server field is an int - a fractional odds limit would fail to
        // decode, so round it here rather than bouncing off the API.
        oddLimits: Math.round(Number(values.oddLimits) || 0),
      };
    });
    const payload: any = {
      OperatorId: this.data.OperatorId,
      Name: (formValue.name || "").trim().toUpperCase(),
      Config: config,
    };
    if (!this.data.add) {
      payload.id = this.data.id;
    }

    const request = this.data.add
      ? this.appService.createOperatorLimit(payload)
      : this.appService.updateOperatorLimit(payload);

    request.subscribe((res) => {
      if (res.status === "RS_OK") {
        this.appService.openSuccessBar("Limit has been saved.");
        this.dialogRef.close(true);
      } else {
        this.appService.openErrorBar(res.errorDescription);
        if (res.errorDescription === "Invalid Token!") {
          sessionStorage.clear();
          this.router.navigateByUrl("/login");
        }
      }
    });
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control) {
        control.markAsTouched();
      }
    });
  }
}

// stakePairValidator keeps min/max usable together: leave both at 0 to inherit
// the operator hierarchy stakes, or set both to at least 10 with max >= min.
// It mirrors validateOperatorLimitConfig on the sap-bo side.
export function stakePairValidator(group: FormGroup) {
  const min = Number(group.get("min").value) || 0;
  const max = Number(group.get("max").value) || 0;
  if (min === 0 && max === 0) {
    return null;
  }
  if (min < 10 || max < 10) {
    return { stakePair: "min/max must be at least 10, or both 0" };
  }
  if (max < min) {
    return { stakePair: "max cannot be lower than min" };
  }
  return null;
}
